import type { FinLabSDK, ModuleManifest } from '@sdk';
import { createChartHost } from './chart-host';
import { data } from './data';
import { debugLog, installGlobalDebugHooks } from './debug';
import { mountDebugPanel } from './debug-panel';
import { loadModuleClient } from './module-loader';
import { persist } from './persist';
import { mountOverview } from './overview';
import { createShell } from './shell';
import './styles.css';

installGlobalDebugHooks();

const app = document.querySelector<HTMLElement>('#app');
if (!app) throw new Error('#app missing');

const shell = createShell(app);
mountDebugPanel(app);
const chart = createChartHost(shell.chartEl);

let unmount: (() => void) | void = () => undefined;

async function listModules(): Promise<ModuleManifest[]> {
  const res = await fetch('/api/modules');
  if (!res.ok) throw new Error(`modules ${res.status}`);
  const json = (await res.json()) as { modules: ModuleManifest[] };
  debugLog('info', 'boot', `modules ${json.modules?.length ?? 0}`, {
    ids: (json.modules || []).map((m) => m.id),
  });
  return json.modules || [];
}

async function activate(id: string, modules: ModuleManifest[]) {
  debugLog('info', 'shell', `activate ${id}`);
  if (typeof unmount === 'function') unmount();
  unmount = undefined;
  chart.clear();
  persist.save('core.activeModule', id);
  shell.renderModules(modules, id, (next) => void activate(next, modules));

  if (id === 'core') {
    unmount = mountOverview({
      shell: { moduleId: 'core', mountPanel: shell.mountPanel, setStatus: shell.setStatus },
      chart,
      data,
      persist,
      debug: debugLog,
    });
    return;
  }

  const manifest = modules.find((m) => m.id === id);
  if (!manifest) {
    shell.setStatus(`unknown module ${id}`, 'err');
    debugLog('error', 'shell', `unknown module ${id}`);
    return;
  }

  shell.setStatus(`loading ${manifest.title}…`, 'idle');
  try {
    const register = await loadModuleClient(id);
    const sdk: FinLabSDK = {
      shell: { moduleId: id, mountPanel: shell.mountPanel, setStatus: shell.setStatus },
      chart,
      data,
      persist,
      debug: debugLog,
    };
    unmount = register(sdk);
    shell.setStatus(manifest.title, 'ok');
    debugLog('info', 'shell', `module ${id} mounted`);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    debugLog('error', 'shell', `module ${id} failed`, { message });
    shell.mountPanel(
      Object.assign(document.createElement('p'), {
        className: 'hint',
        textContent: message,
      }),
    );
    shell.setStatus('module failed', 'err');
  }
}

async function boot() {
  shell.setStatus('booting', 'idle');
  debugLog('info', 'boot', 'start');
  let modules: ModuleManifest[] = [];
  try {
    modules = await listModules();
  } catch (err) {
    const message = err instanceof Error ? err.message : 'cannot list modules';
    shell.setStatus(message, 'err');
    debugLog('error', 'boot', message);
  }
  try {
    const health = await data.healthDeep();
    shell.setHealth(health);
  } catch (err) {
    shell.setHealth(null);
    debugLog('warn', 'boot', 'health/deep failed', {
      message: err instanceof Error ? err.message : String(err),
    });
  }
  const saved = persist.load<string>('core.activeModule', 'leverage-overlay');
  const id =
    saved === 'core' || modules.some((m) => m.id === saved)
      ? saved
      : modules[0]?.id || 'core';
  debugLog('info', 'boot', `opening ${id}`);
  await activate(id, modules);
}

void boot();
